import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Users, Spinner, ArrowSquareOut, EnvelopeSimple, ClockCounterClockwise, FilmStrip, FileImage, X, CursorClick, User, BellRinging, Globe, MapPin, Browser, LinkSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { copyTextToClipboard } from '../../lib/clipboard';

// Human-readable "x minutes/hours/days ago" — keeps the panel scannable
// without bringing in date-fns just for this one helper.
function formatRelativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = (Date.now() - then) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: '2-digit' });
}

/**
 * Canonical absolute-timestamp format for hover tooltips and the "expired on"
 * line: `dd/MM/yyyy hh:mm AM/PM`. Keep it identical across every site in the
 * Recipients dialog so admins can correlate timestamps at a glance.
 */
function formatExactTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh24 = d.getHours();
  const hh12 = hh24 % 12 === 0 ? 12 : hh24 % 12;
  const ampm = hh24 >= 12 ? 'PM' : 'AM';
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy} ${String(hh12).padStart(2, '0')}:${min} ${ampm}`;
}

function formatExpiry(iso, isExpired) {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  // For expired links, show the actual date/time it expired so admins can
  // tell whether it lapsed days ago or yesterday — far more actionable than
  // a plain "expired" pill.
  if (isExpired) return `expired ${formatExactTime(iso)}`;
  const diff = (then - Date.now()) / 1000;
  if (diff < 3600) return 'expires <1h';
  if (diff < 86400) return `expires in ${Math.floor(diff / 3600)}h`;
  return `expires in ${Math.floor(diff / 86400)}d`;
}

// Cheap UA → "Browser on OS" sniff. Shared with AuditLogPage but duplicated
// here to keep the Recipients panel a self-contained component (avoids a
// shared helper module just for two callers).
function summariseUA(ua) {
  if (!ua) return '';
  let browser = 'Browser';
  if (/Edg\//.test(ua)) browser = 'Edge';
  else if (/Firefox\//.test(ua)) browser = 'Firefox';
  else if (/Chrome\//.test(ua) && !/Edg\//.test(ua)) browser = 'Chrome';
  else if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) browser = 'Safari';
  let os = '';
  if (/Windows NT 10/.test(ua)) os = 'Windows 10/11';
  else if (/Windows NT 6/.test(ua)) os = 'Windows';
  else if (/Mac OS X|Macintosh/.test(ua)) os = 'macOS';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/iPhone|iPad/.test(ua)) os = 'iOS';
  else if (/Linux/.test(ua)) os = 'Linux';
  return os ? `${browser} on ${os}` : browser;
}

// Maps wayback_shares status → small coloured pill
function StatusPill({ status, isExpired }) {
  if (isExpired) return <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-muted text-muted-foreground">expired</span>;
  if (status === 'ready') return <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">ready</span>;
  if (status === 'building') return <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-600 dark:text-amber-400 inline-flex items-center gap-1"><Spinner size={9} className="animate-spin" /> rendering</span>;
  if (status === 'failed') return <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-600 dark:text-rose-400">failed</span>;
  return <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">{status || '—'}</span>;
}

// Panel listing every share link generated for the current project, with
// the recipients each one was emailed to. Used by tenant admins for weekly
// audit and as social proof when pitching subscription upgrades.
export function RecipientsPanel({ projectId, projectName, apiCall, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shares, setShares] = useState([]);

  // React 18 StrictMode invokes effects twice on mount, which previously fired
  // the by-project GET twice (no real harm, but ugly in the network log and
  // wasteful on a slow connection). We dedupe with a small in-flight guard
  // keyed by projectId: any second invocation while the same projectId is
  // already loading or just loaded is dropped. AbortController also cancels
  // the first request's response handlers if the panel closes mid-flight.
  const inflightRef = useRef({ projectId: null, controller: null, loadedFor: null });

  const load = useCallback(async () => {
    if (!projectId) return;
    // Drop a duplicate fetch if we're already loading or have already loaded
    // this projectId in this mount cycle. StrictMode's intentional re-invoke
    // is the most common trigger; if the parent later changes projectId the
    // ref above also gets reset (see the useEffect cleanup below).
    if (inflightRef.current.projectId === projectId) return;
    if (inflightRef.current.loadedFor === projectId) return;
    inflightRef.current.projectId = projectId;
    // AbortController is best-effort — axios honours `signal` since 1.x; if
    // the underlying transport doesn't, the unmount fires setState on the
    // captured `aborted` closure below, which is also handled.
    const controller = new AbortController();
    inflightRef.current.controller = controller;
    let aborted = false;
    setLoading(true);
    setError(null);
    try {
      const res = await apiCall(
        'get',
        `/wayback/shares/by-project/${encodeURIComponent(projectId)}`,
        null,
        { signal: controller.signal },
      );
      if (aborted) return;
      setShares(Array.isArray(res?.data?.shares) ? res.data.shares : []);
      inflightRef.current.loadedFor = projectId;
    } catch (e) {
      if (aborted) return;
      // Don't surface aborted-request "errors" as user-visible failures.
      if (controller.signal.aborted || e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') {
        return;
      }
      const detail = e?.response?.data?.detail || e?.message || 'Could not load recipients.';
      setError(typeof detail === 'string' ? detail : 'Could not load recipients.');
    } finally {
      if (!aborted) {
        setLoading(false);
        inflightRef.current.projectId = null;
      }
    }
  }, [projectId, apiCall]);

  useEffect(() => {
    load();
    return () => {
      // On unmount (panel close) cancel any in-flight request so we don't
      // setState after unmount, and reset the loaded guard so re-opening
      // the panel triggers a fresh fetch.
      if (inflightRef.current.controller) {
        try { inflightRef.current.controller.abort(); } catch (_) { /* ignore */ }
      }
      inflightRef.current = { projectId: null, controller: null, loadedFor: null };
    };
  }, [load]);

  const totalEmailed = shares.reduce(
    (acc, s) => acc + ((s.emailed_recipients || []).length),
    0,
  );
  const liveCount = shares.filter((s) => !s.is_expired && s.status !== 'failed').length;
  const totalClicks = shares.reduce((acc, s) => acc + (s.click_count || 0), 0);

  const copyLink = async (token) => {
    const origin = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    const url = `${origin}/api/wayback/share/${token}`;
    const ok = await copyTextToClipboard(url);
    if (ok) toast.success('Link copied');
    else toast.error('Clipboard blocked — open the link in a new tab instead.');
  };

  // Two-day threshold for the "Nudge?" affordance, mirrored from the backend
  // (NUDGE_MIN_AGE_SECONDS). Recipients who haven't opened the link AND were
  // emailed >2 days ago are eligible for a follow-up email.
  const NUDGE_MIN_AGE_MS = 2 * 24 * 60 * 60 * 1000;
  // Track in-flight nudges by rid so the user can click multiple unopened
  // rows in sequence without each click flashing the same spinner.
  const [nudgingRid, setNudgingRid] = useState(null);

  const handleNudge = async (token, rid, recipientName) => {
    if (!token || !rid || nudgingRid) return;
    setNudgingRid(rid);
    try {
      await apiCall('post', `/wayback/share/${token}/r/${rid}/nudge`);
      toast.success(`Nudge sent${recipientName ? ` to ${recipientName}` : ''}`, {
        description: 'Follow-up email with the same time-lapse link is on its way.',
      });
      // Refresh the listing so the new audit row (is_nudge=true) shows up.
      // We bypass the loadedFor guard by resetting it first.
      inflightRef.current.loadedFor = null;
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Could not send the nudge.';
      toast.error(typeof detail === 'string' ? detail : 'Could not send the nudge.');
    } finally {
      setNudgingRid(null);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="recipients-panel">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users size={18} weight="duotone" className="text-primary" />
            Time-lapse recipients
          </DialogTitle>
          <DialogDescription className="text-xs">
            Every share link generated for <strong>{projectName || 'this project'}</strong>{' '}
            and who it was emailed to. Links expire after 7 days.
          </DialogDescription>
        </DialogHeader>

        {/* Top-line stats — quick at-a-glance KPI band */}
        <div className="grid grid-cols-4 gap-2 mt-2" data-testid="recipients-stats">
          <div className="bg-secondary/40 border border-border rounded-md px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider font-bold">Shares</div>
            <div className="text-xl font-bold leading-tight">{shares.length}</div>
          </div>
          <div className="bg-secondary/40 border border-border rounded-md px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider font-bold">Emailed</div>
            <div className="text-xl font-bold leading-tight">{totalEmailed}</div>
          </div>
          <div className="bg-secondary/40 border border-border rounded-md px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider font-bold">Clicks</div>
            <div className="text-xl font-bold leading-tight text-primary" data-testid="recipients-stat-clicks">{totalClicks}</div>
          </div>
          <div className="bg-secondary/40 border border-border rounded-md px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider font-bold">Live</div>
            <div className="text-xl font-bold leading-tight text-emerald-600 dark:text-emerald-400">{liveCount}</div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto -mx-6 px-6 mt-3" data-testid="recipients-list-wrap">
          {loading && (
            <div className="flex items-center justify-center py-10 text-sm" data-testid="recipients-loading">
              <Spinner size={14} className="animate-spin mr-2" /> Loading…
            </div>
          )}

          {!loading && error && (
            <div className="text-[12px] text-rose-500 bg-rose-500/10 border border-rose-500/20 rounded-md px-3 py-2" data-testid="recipients-error">
              {error}
            </div>
          )}

          {!loading && !error && shares.length === 0 && (
            <div className="text-center py-10 text-sm" data-testid="recipients-empty">
              No share links yet. Open the Time-Travel timeline and use{' '}
              <strong>Send to client</strong> to share a time-lapse with a homeowner.
            </div>
          )}

          {!loading && !error && shares.length > 0 && (
            <ul className="space-y-2.5 pb-2" data-testid="recipients-list">
              {shares.map((s) => {
                const rcps = s.emailed_recipients || [];
                const origin = process.env.REACT_APP_BACKEND_URL || window.location.origin;
                const url = `${origin}/api/wayback/share/${s.token}`;
                return (
                  <li
                    key={s.token}
                    className="border border-border rounded-lg p-3 bg-card/60 hover:bg-card/90 transition-colors"
                    data-testid={`recipients-row-${s.token}`}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      {s.extension === 'mp4'
                        ? <FilmStrip size={14} weight="duotone" className="text-primary shrink-0" />
                        : <FileImage size={14} weight="duotone" className="text-primary shrink-0" />}
                      <span className="text-xs font-semibold uppercase tracking-wider">{s.extension || '—'}</span>
                      <StatusPill status={s.status} isExpired={s.is_expired} />
                      {/*
                        Origin badge — shows at a glance how the share was
                        distributed:
                          - "Sent via Email" → tenant used "Send to Client"
                          - "Shared via Link" → tenant used "Copy share link"
                                                / QR code (no recipient row)
                        Detected purely from the doc — `emailed_recipients`
                        is populated only by the email-send path, so empty
                        means it was copied or scanned.
                      */}
                      {(s.emailed_recipients?.length || 0) > 0 ? (
                        <span
                          className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 inline-flex items-center gap-1"
                          data-testid={`recipients-row-${s.token}-origin-email`}
                          title="Sent via Send to Client email"
                        >
                          <EnvelopeSimple size={10} weight="bold" /> Email
                        </span>
                      ) : (
                        <span
                          className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400 inline-flex items-center gap-1"
                          data-testid={`recipients-row-${s.token}-origin-link`}
                          title="Shared via Copy link or QR code"
                        >
                          <LinkSimple size={10} weight="bold" /> Link
                        </span>
                      )}
                      <span
                        className="text-[11px] flex items-center gap-1"
                        title={s.created_at ? `Link generated ${formatExactTime(s.created_at)}` : ''}
                        data-testid={`recipients-row-${s.token}-created`}
                      >
                        <ClockCounterClockwise size={11} /> {formatRelativeTime(s.created_at)}
                      </span>
                      <span className="text-[11px]">·</span>
                      <span
                        className="text-[11px]"
                        title={!s.is_expired && s.expires_at ? `Expires ${formatExactTime(s.expires_at)}` : ''}
                      >{formatExpiry(s.expires_at, s.is_expired)}</span>
                      {/* Click-count pill — only shown if at least one click has
                          landed. Tiny chrome icon + "N opens" + last opened
                          relative time, so the estimator can tell at a glance
                          which links are warm vs untouched. */}
                      {s.click_count > 0 && (
                        <span
                          className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-primary/15 text-primary flex items-center gap-1"
                          title={s.last_clicked_at ? `Last opened ${formatExactTime(s.last_clicked_at)}` : ''}
                          data-testid={`recipients-row-${s.token}-clicks`}
                        >
                          <CursorClick size={11} weight="bold" />
                          {s.click_count} {s.click_count === 1 ? 'open' : 'opens'}
                          {s.last_clicked_at && (
                            <span className="opacity-70 font-normal">· {formatRelativeTime(s.last_clicked_at)}</span>
                          )}
                        </span>
                      )}
                      {/* Hide Copy/Open entirely for expired or failed shares
                          — the buttons are non-actionable in those states and
                          previously sat greyed-out, adding visual noise. */}
                      {!s.is_expired && s.status !== 'failed' && (
                        <div className="ml-auto flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => copyLink(s.token)}
                            className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-secondary hover:bg-secondary/70 transition-colors"
                            title="Copy this share URL to the clipboard"
                            data-testid={`recipients-row-${s.token}-copy`}
                          >
                            Copy
                          </button>
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-primary text-primary-foreground hover:opacity-90 transition-colors flex items-center gap-1"
                            title="Open the share link in a new tab"
                            data-testid={`recipients-row-${s.token}-open`}
                          >
                            Open <ArrowSquareOut size={9} weight="bold" />
                          </a>
                        </div>
                      )}
                    </div>

                    {/* Sender attribution — small "Created by Jane Smith" line
                        directly under the metadata row. Helps tenant admins
                        on a multi-estimator team understand who did the
                        outreach. Hidden when sender_name is unset (legacy docs). */}
                    {s.sender_name && (
                      <div
                        className="mt-1.5 flex items-center gap-1.5 text-[11px] opacity-70"
                        title={s.sender_email || ''}
                        data-testid={`recipients-row-${s.token}-sender`}
                      >
                        <User size={11} weight="bold" />
                        <span>Created by <strong className="font-semibold">{s.sender_name}</strong></span>
                        {s.sender_email && s.sender_email !== s.sender_name && (
                          <span>&lt;{s.sender_email}&gt;</span>
                        )}
                      </div>
                    )}

                    {rcps.length > 0 && (
                      <ul className="mt-2 space-y-1" data-testid={`recipients-row-${s.token}-rcp-list`}>
                        {rcps.map((r, i) => {
                          const hasAccess = r.click_count > 0 && (r.last_open_ip || r.last_open_geo_country);
                          const location = [r.last_open_geo_city, r.last_open_geo_region, r.last_open_geo_country].filter(Boolean).join(', ');
                          const device = summariseUA(r.last_open_user_agent);
                          return (
                          <li
                            key={`${r.recipient_email}-${i}`}
                            // Wrap name-row + access-meta-row in a flex-col so
                            // the location info reads directly under the
                            // recipient it belongs to. The previous "footer
                            // list" was easy to miss.
                            className="flex flex-col gap-0.5 text-[12px]"
                            data-testid={`recipients-row-${s.token}-rcp-${i}`}
                          >
                            <div className="flex items-center gap-2 flex-wrap">
                            <EnvelopeSimple size={11} weight="bold" className="shrink-0" />
                            <span className="font-medium">
                              {r.recipient_name ? `${r.recipient_name} <${r.recipient_email}>` : r.recipient_email}
                            </span>
                            {/* Per-recipient click pill — same look as the
                                share-level pill but scoped to this rid so a
                                tenant admin can see "Sarah opened it 5
                                times, Bob hasn't opened it once" at a glance. */}
                            {r.click_count > 0 ? (
                              <span
                                className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-primary/15 text-primary flex items-center gap-1"
                                title={r.last_clicked_at ? `Last opened ${formatExactTime(r.last_clicked_at)}` : ''}
                                data-testid={`recipients-row-${s.token}-rcp-${i}-clicks`}
                              >
                                <CursorClick size={9} weight="bold" />
                                {r.click_count}
                              </span>
                            ) : (
                              <span className="text-[10px] opacity-40 italic">unopened</span>
                            )}

                            {/* Nudge affordance — shown only when:
                                  - this recipient still hasn't opened the link
                                  - the email is older than 2 days
                                  - it's the ORIGINAL send (not itself a nudge)
                                  - share isn't expired
                                The button is tiny on purpose so it doesn't
                                visually compete with the recipient's name —
                                more of a quiet nudge in the UI itself. */}
                            {(() => {
                              if (r.click_count > 0) return null;
                              if (r.is_nudge) return null; // nudges don't nudge nudges
                              if (s.is_expired) return null;
                              const sentMs = r.sent_at ? new Date(r.sent_at).getTime() : 0;
                              if (!sentMs) return null;
                              const ageMs = Date.now() - sentMs;
                              if (ageMs < NUDGE_MIN_AGE_MS) return null;
                              const busy = nudgingRid === r.rid;
                              return (
                                <button
                                  type="button"
                                  onClick={() => handleNudge(s.token, r.rid, r.recipient_name)}
                                  disabled={busy || !r.rid}
                                  className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-300 hover:bg-amber-500/25 transition-colors flex items-center gap-1 disabled:opacity-50"
                                  title="Send a polite follow-up email — same link, friendlier subject."
                                  data-testid={`recipients-row-${s.token}-rcp-${i}-nudge`}
                                >
                                  {busy
                                    ? <Spinner size={9} className="animate-spin" />
                                    : <BellRinging size={9} weight="bold" />}
                                  {busy ? 'Sending…' : 'Nudge?'}
                                </button>
                              );
                            })()}

                            {/* Visual tag for nudge audit rows so the user
                                understands the second "Sarah" entry isn't a
                                duplicate — it's the follow-up they triggered. */}
                            {r.is_nudge && (
                              <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-300">
                                nudge
                              </span>
                            )}
                            {/* Per-recipient "sent X ago" — hidden when the
                                email went out within 60s of the share's
                                created_at (typical Send-to-client flow where
                                the two events happen back-to-back, leading to
                                a visually-duplicated timestamp). Shown when
                                meaningfully later (e.g. follow-up sends),
                                always available on hover via the title attr
                                regardless. */}
                            {(() => {
                              const sentAt = r.sent_at ? new Date(r.sent_at).getTime() : 0;
                              const createdAt = s.created_at ? new Date(s.created_at).getTime() : 0;
                              const closeToCreation = sentAt && createdAt && Math.abs(sentAt - createdAt) < 60 * 1000;
                              const label = sentAt ? `Emailed ${new Date(sentAt).toLocaleString()}` : '';
                              return (
                                <span
                                  className="opacity-50 text-[10px] ml-auto"
                                  title={label}
                                >
                                  {closeToCreation ? '' : `emailed ${formatRelativeTime(r.sent_at)}`}
                                </span>
                              );
                            })()}
                            </div>
                            {/* IP / approx. location / device — only present
                                when this recipient has actually opened the
                                share since the access-log capture went live.
                                Quiet styling so it doesn't dominate the row,
                                but the data is right under the name where
                                tenant admins expect to find it. */}
                            {hasAccess && (
                              <div
                                className="mt-1 flex items-center gap-2.5 text-[10.5px] text-muted-foreground flex-wrap"
                                data-testid={`recipients-row-${s.token}-rcp-${i}-access`}
                              >
                                <span className="opacity-80 font-medium">Last opened from:</span>
                                {r.last_open_ip && (
                                  <span className="inline-flex items-center gap-1 font-mono font-medium text-foreground" title="Client IP from latest open">
                                    <Globe size={10} weight="bold" />{r.last_open_ip}
                                  </span>
                                )}
                                {location && (
                                  <span className="inline-flex items-center gap-1 font-medium text-foreground" title="Approx. location from IP">
                                    <MapPin size={10} weight="bold" />{location}
                                  </span>
                                )}
                                {device && (
                                  <span className="inline-flex items-center gap-1" title={r.last_open_user_agent}>
                                    <Browser size={10} weight="bold" />{device}
                                  </span>
                                )}
                              </div>
                            )}
                          </li>
                          );
                        })}
                      </ul>
                    )}
                    {/* Share-level access metadata — surfaces the IP, City,
                        State, Country and device for the most recent
                        UNATTRIBUTED open (i.e. someone hit the link via
                        Copy-share-link or the QR code, no recipient row).
                        Only rendered when the share's overall click_count is
                        non-zero AND we have at least one access datum back
                        from the audit log. Per-recipient access lines (when
                        the open went through an emailed link) appear above,
                        inside their own list. */}
                    {(s.click_count > 0) && (s.last_open_ip || s.last_open_geo_country) && (
                      <div
                        className="mt-2 flex items-center gap-2.5 text-[10.5px] text-muted-foreground flex-wrap"
                        data-testid={`recipients-row-${s.token}-share-access`}
                      >
                        <span className="opacity-80 font-medium">Last opened from:</span>
                        {s.last_open_ip && (
                          <span className="inline-flex items-center gap-1 font-mono font-medium text-foreground" title="Client IP from latest open">
                            <Globe size={10} weight="bold" />{s.last_open_ip}
                          </span>
                        )}
                        {(s.last_open_geo_city || s.last_open_geo_region || s.last_open_geo_country) && (
                          <span className="inline-flex items-center gap-1 font-medium text-foreground" title="Approx. location from IP">
                            <MapPin size={10} weight="bold" />
                            {[s.last_open_geo_city, s.last_open_geo_region, s.last_open_geo_country].filter(Boolean).join(', ')}
                          </span>
                        )}
                        {s.last_open_user_agent && summariseUA(s.last_open_user_agent) && (
                          <span className="inline-flex items-center gap-1" title={s.last_open_user_agent}>
                            <Browser size={10} weight="bold" />{summariseUA(s.last_open_user_agent)}
                          </span>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
